<script setup lang="ts">
import { onMounted, PropType } from 'vue'
import { useRoute } from 'vue-router'

/* Types */
import { Category } from '@/types/api/api-v3/category'

const emit = defineEmits(['isCategoryActive'])

const props = defineProps({
  category: {
    required: true,
    type: Object as PropType<Category>,
  },
  isCategoryPage: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const route = useRoute()

const { isDesktop } = useDevice()

const isErrorImage = ref(false)

const productImage = computed(() => {
  if (isErrorImage.value || !props.category.image) {
    return './no-image-category.svg'
  }

  return props.category.image
})

const isCategoryActive = computed(() => {
  return props.category.path === route.path
})

onMounted(() => {
  if (isCategoryActive.value) {
    emit('isCategoryActive')
  }
})

const slideItemClasses = computed(() => {
  return [
    isDesktop ? 'categories-item' : 'categories-item--mobile',
    props.category.path === route.path ? 'active--category' : '',
    { 'categories-item-page': props.isCategoryPage },
  ]
})

const isSale = computed(() => props.category.slug === 'sales')
</script>

<template>
  <nuxt-link
    v-ripple
    :to="category.path"
    itemprop="url"
    :class="slideItemClasses"
    :prefetch="false"
  >
    <nuxt-img
      v-if="!isCategoryPage && !isSale"
      :alt="category.slug"
      :src="productImage"
      itemprop="image"
      class="categories-item__image"
      @error="isErrorImage = true"
    />
    <nuxt-img
      v-else-if="!isCategoryPage"
      :alt="category.slug"
      :src="productImage"
      itemprop="image"
      class="categories-item__image"
      @error="isErrorImage = true"
    />

    <span itemprop="name" class="categories-item__title">
      {{ category.name }}
    </span>
  </nuxt-link>
</template>

<style lang="scss" scoped>
.categories-item {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  overflow: hidden;
  background: #f9fafb;
  border-radius: 20px;

  &__title {
    width: 100%;
    padding: 0 0 25px 12px;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 800;
    line-height: 14px;
    color: $text-dark-gray;

    @include media('>=desktop') {
      white-space: pre-wrap;
    }
  }

  &-page {
    height: 30px !important;
    margin-right: 8px !important;

    .categories-item__title {
      padding: 7px 12px;
      margin-top: 0;
    }
  }

  @include media('>=tablet') {
    width: 90px;
    height: 112px;

    &:nth-child(1n) {
      margin-right: 12px;
    }
  }

  &:hover {
    cursor: pointer;
  }

  &__image {
    width: 76px;
    height: 60px;
    margin-top: 4px;

    @include media('>=phone') {
      width: 92px;
      height: 72px;
    }
    @include media('>=desktop') {
      width: 80px;
      height: 62px;
    }
  }
}

.categories-item--mobile {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
  height: 107px;
  padding-bottom: 4px;
  margin-right: 0;
  background: #f9fafb;
  border-radius: 20px;

  @include media('<=desktop-mini') {
    padding-bottom: 0;
  }

  @include media('>=phone') {
    height: 131px;
  }
  @include media('>=portrait-phone') {
    height: 190px;
  }

  &__title {
    margin-top: 14px;

    @include media('>=phone') {
      margin-top: 18px;
    }

    @include media('>=portrait-phone') {
      margin-top: 24px;
    }
  }
}

.active--category {
  background-color: #fff3ee;
  border-radius: 20px;

  .categories-item__title {
    color: $primary;
  }
}
</style>
